import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 41. AUDIT LOG  &  42. SOFT DELETE
// Audit : on enregistre les actions importantes (qui, quoi, avant/après).
// Les logs sont protégés : écriture + lecture seulement, jamais modifiables
// par les utilisateurs standards (RLS sans policy UPDATE/DELETE).
// Soft delete : on évite la suppression physique immédiate des données
// importantes (deleted_at / deleted_by / archived_at / archived_by) et on
// permet la restauration.
// ─────────────────────────────────────────────────────────────

export const AUDIT_ACTIONS: { key: string; label: string; icon: string }[] = [
  { key: 'create', label: 'Création', icon: 'ri-add-circle-line' },
  { key: 'update', label: 'Modification', icon: 'ri-edit-line' },
  { key: 'delete', label: 'Suppression', icon: 'ri-delete-bin-line' },
  { key: 'restore', label: 'Restauration', icon: 'ri-arrow-go-back-line' },
  { key: 'archive', label: 'Archivage', icon: 'ri-archive-line' },
  { key: 'login', label: 'Connexion', icon: 'ri-login-box-line' },
  { key: 'payment', label: 'Paiement', icon: 'ri-money-dollar-circle-line' },
];

export function auditActionMeta(key: string | null | undefined) {
  const base = (key || '').split('.')[0];
  return AUDIT_ACTIONS.find((a) => a.key === base) || AUDIT_ACTIONS[1];
}

export interface CoreAuditEntry {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  user_id: number | null;
  user_name: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string | null;
}

export interface AuditInput {
  tenantId: number | null;
  organizationId: number;
  userId?: number | null;
  userName?: string | null;
  action: string;
  resourceType?: string | null;
  resourceId?: string | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  ip?: string | null;
}

export async function logAudit(input: AuditInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_audit_log')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId ?? null,
      user_name: input.userName ?? null,
      action: input.action,
      resource_type: input.resourceType ?? null,
      resource_id: input.resourceId != null ? String(input.resourceId) : null,
      before_data: input.before ?? null,
      after_data: input.after ?? null,
      ip: input.ip ?? null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function listAuditLog(
  organizationId: number,
  filter?: { action?: string; resourceType?: string; limit?: number },
): Promise<CoreAuditEntry[]> {
  let query = supabase
    .from('core_audit_log')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(filter?.limit ?? 100);
  if (filter?.action) query = query.ilike('action', `${filter.action}%`);
  if (filter?.resourceType) query = query.eq('resource_type', filter.resourceType);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreAuditEntry[];
}

export interface AuditStats {
  total: number;
  byAction: Record<string, number>;
  byResource: Record<string, number>;
}

export interface RecentActionRow {
  id: number;
  at: string;
  kind: 'create' | 'update' | 'delete' | 'payment' | 'other';
  icon: string;
  actorUserId: number | null;
  actorName: string | null;
  title: string;
  detail: string | null;
}

/**
 * Nombre d'actions récentes par utilisateur (audit + timeline) sur les
 * `sinceDays` derniers jours. Sert à afficher un compteur sur la carte de
 * chaque travailleur. Best-effort : renvoie {} en cas d'erreur.
 */
export async function fetchRecentActionCounts(
  organizationId: number,
  sinceDays = 30,
): Promise<Record<number, number>> {
  const since = new Date(Date.now() - sinceDays * 86_400_000).toISOString();
  const counts: Record<number, number> = {};
  const bump = (raw: unknown) => {
    const uid = Number(raw);
    if (Number.isFinite(uid) && uid > 0) counts[uid] = (counts[uid] || 0) + 1;
  };
  try {
    const { data } = await supabase
      .from('core_audit_log')
      .select('user_id')
      .eq('organization_id', organizationId)
      .gte('created_at', since);
    (data || []).forEach((r) => bump((r as { user_id: number | null }).user_id));
  } catch { /* best-effort */ }
  try {
    const { data } = await supabase
      .from('core_activity_timeline')
      .select('actor_user_id')
      .eq('organization_id', organizationId)
      .gte('occurred_at', since);
    (data || []).forEach((r) => bump((r as { actor_user_id: number | null }).actor_user_id));
  } catch { /* best-effort */ }
  return counts;
}

export async function fetchAuditStats(organizationId: number): Promise<AuditStats> {
  const { data } = await supabase
    .from('core_audit_log')
    .select('action, resource_type')
    .eq('organization_id', organizationId);
  const byAction: Record<string, number> = {};
  const byResource: Record<string, number> = {};
  (data || []).forEach((r) => {
    const row = r as { action: string; resource_type: string | null };
    const base = (row.action || '').split('.')[0];
    byAction[base] = (byAction[base] || 0) + 1;
    const res = row.resource_type || 'other';
    byResource[res] = (byResource[res] || 0) + 1;
  });
  return { total: (data || []).length, byAction, byResource };
}

// ── 42. SOFT DELETE ──

export interface SoftDeleteTableDef {
  table: string;
  label: string;
  icon: string;
  entityType: string;
}

/** Tables du socle gérant le soft delete (archives restaurables). */
export const SOFT_DELETE_TABLES: SoftDeleteTableDef[] = [
  { table: 'core_customers', label: 'Clients', icon: 'ri-user-star-line', entityType: 'customer' },
  { table: 'core_contacts', label: 'Contacts', icon: 'ri-contacts-line', entityType: 'contact' },
  { table: 'core_professionals', label: 'Professionnels', icon: 'ri-user-2-line', entityType: 'professional' },
  { table: 'core_notes', label: 'Notes', icon: 'ri-sticky-note-line', entityType: 'note' },
  { table: 'core_conversations', label: 'Conversations', icon: 'ri-chat-3-line', entityType: 'conversation' },
  { table: 'core_tags', label: 'Tags', icon: 'ri-hashtag', entityType: 'tag' },
  { table: 'core_calendar_events', label: 'Événements', icon: 'ri-calendar-event-line', entityType: 'event' },
];

export function softDeleteTableMeta(table: string) {
  return SOFT_DELETE_TABLES.find((t) => t.table === table) || null;
}

export interface DeletedRow {
  id: number;
  label: string;
  deleted_at: string | null;
  deleted_by: number | null;
  archived_at: string | null;
  raw: Record<string, unknown>;
}

function rowLabel(table: string, row: Record<string, unknown>): string {
  const candidates: Record<string, string[]> = {
    core_customers: ['display_name', 'company_name', 'email'],
    core_contacts: ['company_name', 'first_name', 'last_name', 'email'],
    core_professionals: ['display_name', 'profession'],
    core_notes: ['title', 'body'],
    core_conversations: ['subject'],
    core_tags: ['name'],
    core_calendar_events: ['title'],
  };
  const keys = candidates[table] || ['name', 'title'];
  for (const k of keys) {
    const v = row[k];
    if (v != null && String(v).trim()) return String(v);
  }
  return `#${row.id}`;
}

export async function softDeleteRow(table: string, id: number, userId?: number | null): Promise<void> {
  const { error } = await supabase
    .from(table)
    .update({ deleted_at: new Date().toISOString(), deleted_by: userId ?? null })
    .eq('id', id);
  if (error) throw error;
}

export async function restoreRow(table: string, id: number): Promise<void> {
  const { error } = await supabase
    .from(table)
    .update({ deleted_at: null, deleted_by: null, archived_at: null, archived_by: null })
    .eq('id', id);
  if (error) throw error;
}

export async function archiveRow(table: string, id: number, userId?: number | null): Promise<void> {
  const { error } = await supabase
    .from(table)
    .update({ archived_at: new Date().toISOString(), archived_by: userId ?? null })
    .eq('id', id);
  if (error) throw error;
}

/** Suppression physique (uniquement pour les données légalement/techniquement supprimables). */
export async function purgeRow(table: string, id: number): Promise<void> {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

export async function listDeletedRows(table: string, organizationId: number): Promise<DeletedRow[]> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('organization_id', organizationId)
    .not('deleted_at', 'is', null)
    .order('deleted_at', { ascending: false });
  if (error) return [];
  return (data || []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      id: Number(row.id),
      label: rowLabel(table, row),
      deleted_at: (row.deleted_at as string) ?? null,
      deleted_by: row.deleted_by != null ? Number(row.deleted_by) : null,
      archived_at: (row.archived_at as string) ?? null,
      raw: row,
    };
  });
}

/** Nombre total d'éléments en corbeille (toutes tables gérées). */
export async function fetchTrashCounts(organizationId: number): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  await Promise.all(
    SOFT_DELETE_TABLES.map(async (t) => {
      const { count } = await supabase
        .from(t.table)
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null);
      counts[t.table] = count || 0;
    }),
  );
  return counts;
}